import axios from 'axios'
import shopServer from '../../services/shoe'
import { convertArrayItems, convertObjectFields } from '@/utils/textConverter'

// 全局配置
const config = {
  // 默認使用HTTP協議，可以通過環境變量或本地存儲來切換
  useHttps: false,  // 设置为 false 表示使用 HTTP
  apiHost: process.env.NODE_ENV === 'production' ? 'backend' : 'localhost',
  apiPort: 8000,
  apiTimeout: 60000, // 60秒超時
  // 啟用詳細日誌
  enableDetailedLogs: true
}

// 根據配置生成API基本URL - 修改這裡，使用完整路徑而不是相對路徑
const API_URL = '/api/v1'

// 创建API请求实例
const api = axios.create({
  baseURL: API_URL,
  timeout: config.apiTimeout,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// 添加请求拦截器，用于调试
api.interceptors.request.use(config => {
  // 添加额外的请求头标记
  config.headers['X-Debug-Info'] = 'client-debug';
  return config;
}, error => {
  console.error('API请求配置错误:', error);
  return Promise.reject(error);
});

// 添加响应拦截器，用于调试
api.interceptors.response.use(response => {
  return response;
}, error => {
  return Promise.reject(error);
});

// 初始状态
const state = {
  shoes: [],
  currentShoe: null,
  collections: [],
  loading: false,
  error: null,
  totalShoes: 0,
  filters: {
    search: '',
    gender: '',
    collection: '',
    tags:'',
    kind:'',
    brand:'',
    sortBy: 'created_at',
    sortOrder: -1
  },
  // 添加用于调试的状态
  lastApiResponse: null,
  apiRequestsCount: 0,
  // 添加網絡配置和狀態
  config: { ...config },
  networkStatus: typeof navigator !== 'undefined' ? navigator.onLine : true
}

// 获取器
const getters = {
  allShoes: state => state.shoes,
  currentShoe: state => state.currentShoe,
  allCollections: state => state.collections,
  isLoading: state => state.loading,
  hasError: state => !!state.error,
  errorMessage: state => state.error,
  totalShoesCount: state => state.totalShoes,
  filters: state => state.filters,
  // 添加调试相关的getter
  lastApiResponse: state => state.lastApiResponse,
  apiRequestsCount: state => state.apiRequestsCount,
  // 添加配置相關的getter
  apiConfig: state => state.config,
  isOnline: state => state.networkStatus
}

// 动作
const actions = {
  // 更新網絡狀態
  updateNetworkStatus({ commit }, isOnline) {
    commit('setNetworkStatus', isOnline);
  },
  
  // 更新API配置
  updateApiConfig({ commit }, newConfig) {
    commit('setApiConfig', newConfig);
  },
  
  // 获取鞋子列表
  async fetchShoes({ commit, state }, payload = {}) {
    try {
      commit('setLoading', true)
      commit('clearError')
      commit('incrementApiRequests')

      const {
        page = 1,
        limit = 20,
        refresh = true, // 你的 params 構建中似乎沒有使用 refresh
        brand: payloadBrand,
        kind: payloadKind,
        sortBy: payloadSortBy,
        sortOrder: payloadSortOrder
      } = payload;

      const skip = (page - 1) * limit
      let params = { 
        skip, 
        limit,
        sort_by: payloadSortBy !== undefined ? payloadSortBy : state.filters.sortBy,
        sort_order: payloadSortOrder !== undefined ? payloadSortOrder : state.filters.sortOrder
      }
      
      // 添加过滤条件
      if (state.filters.search) {
        params.search = state.filters.search
      }
      
      if (state.filters.gender) {
        params.gender = state.filters.gender 
      }
      if (state.filters.collection) {
        params.collection_id = state.filters.collection
      }
      if (state.filters.tags) {
        params.tags = state.filters.tags 
      }
      if (payloadBrand !== undefined) { 
        params.brandName = payloadBrand;
      } else if (state.filters.brand) { 
        params.brandName = state.filters.brand;
      }
      if (payloadKind !== undefined) { 
        params.kind = payloadKind;
      } else if (state.filters.kind) { 
        params.kind = state.filters.kind;
      }
      
      // 修改这里，使用API实例而不是直接axios，避免重复路径(获取所有数据)
      // let response = await api.get('/shoes/', { params });
      let response = await shopServer.GetGoodsList(params);
      
      // 将响应保存到store中用于调试
      commit('setLastApiResponse', {
        url: '/shoes/',
        params,
        status: response.status,
        data: response.data
      });
      
      // 确保处理正确的API响应结构 - 后端API返回的是 { items, total }
      const responseData = response.data || {}
      
      // 检查数据格式，首先查找items字段，如果不存在则尝试查找shoes字段（后向兼容）
      const items = Array.isArray(responseData.items) ? responseData.items : 
                   (Array.isArray(responseData.shoes) ? responseData.shoes : [])
      
      const total = responseData.total || 0
      
      // 更新状态
      if (refresh) {
        commit('setShoes', items)
      } else {
        commit('appendShoes', items)
      }
      commit('setTotalShoes', total)
      return { success: true, data: items }
    } catch (error) {
      commit('setError', error.message || '无法加载鞋子列表')
      return { success: false, error: error.message }
    } finally {
      commit('setLoading', false)
    }
  },
  
  // 获取分类列表
  async fetchCollections({ commit }, payload = {}) {
    const kind = payload.kind
    const gender = payload.gender
    try {
      commit('setLoading', true)
      commit('clearError')
      commit('incrementApiRequests')
      
      const params = {}
      if (kind) {
        params.kind = kind 
      }
      if (gender) {
        params.gender = gender 
      }
      // 修改這裡，使用API實例而不是直接axios，避免重複路徑
      // let response = await api.get('/shoes/collections/all');
      let response = await shopServer.GetGoodsCategory(params);
      
      // 将响应保存到store中用于调试
      commit('setLastApiResponse', {
        url: '/shoes/collections/all',
        status: response.status,
        data: response.data
      });
      
      const collections = response.data || []
      
      commit('setCollections', collections)
      return { success: true, data: collections }
    } catch (error) {
      console.error('获取分类列表失败:', error)
      commit('setError', error.message || '无法加载分类列表')
      return { success: false, error: error.message }
    } finally {
      commit('setLoading', false)
    }
  },
  
  // 获取单个鞋子详情
  async fetchShoeDetail({ commit }, id) {
    if (!id) {
      commit('setError', '无效的鞋子ID')
      return { success: false, error: '无效的鞋子ID' }
    }
    
    try {
      commit('setLoading', true)
      commit('clearError')
      commit('incrementApiRequests')
      
      // 使用API实例发起请求
      // const response = await api.get(`/shoes/${id}`)
      const response = await shopServer.GetGoodsDetail(id)
      // 将响应保存到store中用于调试
      commit('setLastApiResponse', {
        url: `/shoes/${id}`,
        status: response.status,
        data: response.data
      });
      
      const shoe = response.data
      
      commit('setCurrentShoe', shoe)
      return { success: true, data: shoe }
    } catch (error) {
      console.error(`获取鞋子详情失败 (ID: ${id}):`, error)
      commit('setError', error.message || '无法加载鞋子详情')
      return { success: false, error: error.message }
    } finally {
      commit('setLoading', false)
    }
  },
  
  // 更新筛选条件
  updateFilters({ commit, dispatch }, filters) {
    commit('setFilters', filters)
    return dispatch('fetchShoes', { page: 1, refresh: true })
  },
  
  // 清除当前展示的鞋子
  clearCurrentShoe({ commit }) {
    commit('setCurrentShoe', null)
    return { success: true }
  }
}

// 变更
const mutations = {
  setShoes(state, shoes) {
    state.shoes = convertArrayItems(shoes)
  },
  appendShoes(state, shoes) {
    state.shoes = [...state.shoes, ...convertArrayItems(shoes)]
  },
  setCurrentShoe(state, shoe) {
    state.currentShoe = shoe ? convertObjectFields(shoe) : null
  },
  setCollections(state, collections) {
    state.collections = convertArrayItems(collections)
  },
  setTags(state, tags) {
    state.tags = convertArrayItems(tags)
  },
  setBrands(state, brands) {
    state.brands = convertArrayItems(brands) 
  },
  setLoading(state, isLoading) {
    state.loading = isLoading
  },
  setError(state, error) {
    state.error = error
  },
  clearError(state) {
    state.error = null
  },
  setTotalShoes(state, total) {
    state.totalShoes = total
  },
  setFilters(state, filters) {
    state.filters = { ...state.filters, ...filters }
  },
  // 添加用于调试的mutations
  setLastApiResponse(state, response) {
    state.lastApiResponse = response
  },
  incrementApiRequests(state) {
    state.apiRequestsCount++
  },
  // 添加网络和配置相关的mutations
  setNetworkStatus(state, isOnline) {
    state.networkStatus = isOnline
  },
  setApiConfig(state, newConfig) {
    state.config = { ...state.config, ...newConfig }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
} 